import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import useScroll from 'useScroll';
import PropTypes from 'prop-types';
import {
  Label,
  TextField,
  Select,
  MenuItem,
  Calendar,
  ProgressBar,
  DateUtils,
} from 'mw-style-react';
import cn from 'classnames';
import AppUtils from '@control-front-end/utils/utils';
import {
  GET_TRANSACTIONS,
  SEARCH_TRANSACTIONS,
  REMOVE_TRANSACTION,
  CREATE_TRANSACTION,
} from '@control-front-end/common/constants/actorAccounts';
import useIntl from 'useIntl';
import mes from './intl';
import TransactionsList from './components/TransactionsList';
import './Transactions.scss';

export const DATE_RANGES = [
  'allTime',
  'custom',
  'today',
  'yesterday',
  'lastWeek',
  'lastMonth',
  'previousMonth',
];

const initState = {
  list: [],
  limit: 15,
  offset: 0,
  endList: false,
};

function Transactions(props) {
  const {
    actorId,
    nameId,
    currencyId,
    currencyName,
    accountType,
    debit,
    credit,
    incomeType,
    readOnly,
  } = props;
  const t = useIntl();
  const listBox = useRef();
  const dispatch = useDispatch();
  const [transactions, setTransactions] = useState(initState);
  const [query, setQuery] = useState('');
  const [rangeType, setRangeType] = useState('allTime');
  const [dateRange, setDateRange] = useState({});
  const [loading, setLoading] = useState(false);
  const [showHelperText, toggleHelperText] = useState(false);
  const todayDate = DateUtils.unixtime();

  /**
   * Поиск транзакций
   */
  const searchTransactions = (value) => {
    if (value.length < 2) return;
    dispatch({
      type: SEARCH_TRANSACTIONS.REQUEST,
      payload: {
        actorId,
        nameId,
        currencyId,
        incomeType,
        accountType,
        q: value,
      },
      callback: (data) => setTransactions({ ...initState, list: data }),
    });
  };

  /**
   * Получить транзакции
   */
  const getData = (loadMore = false) => {
    if (loadMore && transactions.endList) return;
    setLoading(true);
    const { from, to } = dateRange;
    dispatch({
      type: GET_TRANSACTIONS.REQUEST,
      payload: {
        actorId,
        nameId,
        currencyId,
        incomeType,
        accountType,
        transactions: loadMore ? transactions : initState,
        onlyRoot: 'false',
        debit,
        credit,
        loadMore,
        from,
        to,
      },
      callback: (data) => {
        setTransactions(data);
        setLoading(false);
      },
    });
  };

  /**
   *  Удалить транзакцию
   */
  const handleRemove = (id) => {
    setLoading(true);
    dispatch({
      type: REMOVE_TRANSACTION.REQUEST,
      payload: { id, transactions, accountType, nameId, currencyId },
      callback: (data) => {
        setTransactions(data);
        setLoading(false);
      },
    });
  };

  /**
   * Создать корректирующую транзакцию
   */
  const handleCorrect = ({ id, incomeType: incType, transaction }) => {
    const key = props[incType].key;
    dispatch({
      type: CREATE_TRANSACTION.REQUEST,
      payload: { id, key, incomeType: incType, accountType, transaction },
      callback: () => {
        setTransactions(initState);
        getData();
      },
    });
  };

  useEffect(() => {
    setTransactions(initState);
    if (query.length > 1) {
      searchTransactions(query);
    } else {
      getData();
    }
  }, [actorId, incomeType, dateRange]);
  useScroll({
    ref: listBox,
    callback: () => {
      if (!query.length) getData(true);
    },
  });

  /**
   * Очистка строки поиска
   */
  const clearSearch = (needReload = false) => {
    if (!query.length) return;
    toggleHelperText(false);
    setQuery('');
    if (needReload) getData();
  };

  /**
   * Установка диапазона дат по умолчанию
   */
  const resetRange = () => {
    if (rangeType === 'allTime') return;
    setRangeType('allTime');
    setDateRange({});
  };

  /**
   * Обработка смены типа диапазона
   */
  const handleChangeRange = (value) => {
    clearSearch();
    const newDateRange =
      value !== 'allTime' ? AppUtils.getRangeDate(value) : {};
    setRangeType(value);
    setDateRange(newDateRange);
  };

  /**
   * Обработка смены даты
   */
  const handleChangeDate = ({ value }) => {
    clearSearch();
    setRangeType('custom');
    setDateRange({
      from: value.startDate,
      to: value.endDate,
    });
  };

  /**
   * Обработка ввода строки поиска
   */
  const handleChangeQuery = ({ value }) => {
    resetRange();
    if (value.length === 0) clearSearch(true);
    if (value.length > 1) toggleHelperText(true);
    setQuery(value);
  };

  /**
   * Обработка нажатия клавиш
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && query.length > 1) {
      searchTransactions(query);
      toggleHelperText(false);
    }
  };

  const renderDateRange = () => {
    const { from: startDate, to: endDate } = dateRange;
    let value;
    if (startDate && endDate) {
      const start = DateUtils.toDate(startDate, 'm DD, YYYY; HH:mm');
      const end = DateUtils.toDate(endDate, 'm DD, YYYY; HH:mm');
      value = `${start} - ${end}`;
    }

    return (
      <TextField
        styleName="transactions__range"
        size="medium"
        bordered={true}
        value={value || ''}
        placeholder={t(mes.selectRange)}
        visibility={rangeType === 'allTime' ? 'hidden' : 'visible'}
        calendar={() => (
          <Calendar
            styleName="transactions__range__calendar"
            size="small"
            value={{ startDate, endDate }}
            onChange={handleChangeDate}
            maxDate={todayDate}
            time={true}
            dateRange={true}
          />
        )}
      />
    );
  };

  const emptyList = (
    <div styleName="transactions__content__empty">
      <div styleName="transactions__content__empty__title">
        <Label fontWeight="semibold" value={t(mes.noTransactions)} />
      </div>
      <div styleName="transactions__content__empty__details">
        <Label value={t(mes.noTransactionsDesc)} />
      </div>
    </div>
  );

  const renderContent = () => {
    if (!transactions.list.length && loading) {
      return (
        <div styleName="transactions__content__loader">
          <ProgressBar type="circle" size="large" />
        </div>
      );
    }
    if (transactions.list.length || !query.length) {
      return (
        <TransactionsList
          debitId={debit.id}
          creditId={credit.id}
          transactions={transactions.list}
          currencyName={currencyName}
          showTotal={false}
          readOnly={readOnly}
          handleChange={(data) =>
            setTransactions({ ...transactions, list: data })
          }
          handleRemove={handleRemove}
          handleCorrect={handleCorrect}
        />
      );
    }
    return emptyList;
  };

  return (
    <div styleName="transactions">
      <div styleName="transactions__filters">
        <TextField
          styleName={cn('transactions__search', { help: showHelperText })}
          leftIcon="search"
          size="large"
          rightIcon="close"
          bordered="bordered"
          autoSelect={true}
          value={query}
          placeholder={t(mes.search)}
          onChange={handleChangeQuery}
          onClickRightIcon={() => clearSearch(true)}
          onKeyDown={handleKeyDown}
        />
        <Select
          styleName="transactions__range"
          bordered={true}
          value={rangeType}
          onChange={({ value }) => handleChangeRange(value)}
        >
          {DATE_RANGES.map((item) => (
            <MenuItem key={item} value={item} label={t(mes[`range_${item}`])} />
          ))}
        </Select>
        {rangeType !== 'allTime' ? renderDateRange() : null}
      </div>
      <div styleName="transactions__content" ref={listBox}>
        {renderContent()}
        {transactions.list.length && loading ? (
          <div styleName="transactions__content__smallloader">
            <ProgressBar />
          </div>
        ) : null}
      </div>
    </div>
  );
}

Transactions.propTypes = {
  actorId: PropTypes.string,
  nameId: PropTypes.string,
  currencyId: PropTypes.string,
  currencyName: PropTypes.string,
  debit: PropTypes.object,
  credit: PropTypes.object,
  incomeType: PropTypes.string,
  accountType: PropTypes.oneOf(['fact', 'plan']),
  readOnly: PropTypes.bool,
};

export default Transactions;
